import { useCallback, useRef } from "react";
import { Plus, Upload } from "lucide-react";
import { img } from "@/lib/api";
import { CoverView } from "@/components/PageView";
import PageView from "@/components/PageView";
import { LowResDialog, useUploader } from "@/components/UploadStep";

export function PageStrip({ book, photos, sel, setSel, onReorder, onAdd }) {
  const dragFrom = useRef(null);
  return (
    <div className="flex gap-3 overflow-x-auto pb-2 lg:flex-col lg:overflow-y-auto lg:overflow-x-visible lg:pb-0" data-testid="editor-page-strip">
      <button data-testid="editor-page-thumbnail-cover" onClick={() => setSel(-1)}
        className={`w-20 shrink-0 lg:w-full ${sel === -1 ? "ring-2 ring-[#3B5BDB] ring-offset-2 ring-offset-[#FDFBF7]" : ""}`}>
        <CoverView book={book} photos={photos} enhance={book.enhance} small />
        <p className="mt-1 text-center text-[10px] text-[#8C8498]">Cover</p>
      </button>
      {book.pages.map((p, i) => (
        <button key={p.id} data-testid={`editor-page-thumbnail-${i}`} draggable
          onDragStart={() => (dragFrom.current = i)}
          onDragOver={(e) => e.preventDefault()}
          onDrop={() => { if (dragFrom.current !== null && dragFrom.current !== i) onReorder(dragFrom.current, i); dragFrom.current = null; }}
          onClick={() => setSel(i)}
          className={`w-20 shrink-0 cursor-grab bg-white lg:w-full ${sel === i ? "ring-2 ring-[#3B5BDB] ring-offset-2 ring-offset-[#FDFBF7]" : "opacity-90 hover:opacity-100"}`}>
          <div className="pointer-events-none border border-[#E8E2D9]"><PageView page={p} photos={photos} size={book.size} theme={book.theme} enhance={book.enhance} small /></div>
          <p className="mt-1 text-center text-[10px] text-[#8C8498]">{i + 1}</p>
        </button>
      ))}
      <button data-testid="editor-add-page-btn" onClick={onAdd} className="grid w-20 shrink-0 place-items-center rounded-sm border-2 border-dashed border-[#E8E2D9] py-6 text-[#8C8498] hover:border-[#3B5BDB] hover:text-[#3B5BDB] lg:w-full">
        <Plus className="h-5 w-5" />
      </button>
    </div>
  );
}

export function PhotoTray({ list, used, enhance, onPick, bookId, onUploaded, compact }) {
  const input = useRef();
  const { upload, progress, rejected, setRejected } = useUploader(bookId, useCallback(onUploaded, [onUploaded]));
  const sorted = [...list].sort((a, b) => (used.has(a.id) - used.has(b.id)) || (b.curated - a.curated));
  return (
    <div data-testid="editor-photo-tray">
      <div className="mb-3 flex items-center justify-between">
        <p className="overline">Photos · {list.length}/100</p>
        <button data-testid="editor-upload-more-btn" disabled={progress !== null || list.length >= 100} onClick={() => input.current.click()} className="flex items-center gap-1 text-xs font-medium text-[#3B5BDB] hover:underline disabled:opacity-50">
          <Upload className="h-3.5 w-3.5" />{progress !== null ? `${progress}%` : "Add more"}
        </button>
        <input ref={input} type="file" multiple accept="image/jpeg,image/png,image/webp" hidden data-testid="editor-upload-input"
          onChange={(e) => { upload(e.target.files, list.length); e.target.value = ""; }} />
      </div>
      <div className={`grid gap-1.5 ${compact ? "grid-cols-4 sm:grid-cols-6" : "grid-cols-3"}`}>
        {sorted.map((p) => (
          <button key={p.id} data-testid={`tray-photo-${p.id}`} draggable onDragStart={(e) => e.dataTransfer.setData("photo-id", p.id)}
            onClick={() => onPick?.(p.id)} className="group relative aspect-square overflow-hidden rounded-sm bg-[#F5F0E8]">
            <img src={img(enhance ? p.url_enhanced : p.url)} alt={p.filename} className={`h-full w-full object-cover transition-transform group-hover:scale-105 ${used.has(p.id) ? "opacity-45" : ""}`} />
            {used.has(p.id) ? (
              <span className="absolute bottom-1 left-1 rounded bg-white/85 px-1 text-[9px] font-medium">In book</span>
            ) : !p.curated ? (
              <span className="absolute bottom-1 left-1 rounded bg-[#1E1B24]/75 px-1 text-[9px] text-white" title="AI set this aside (duplicate or blurry). Add it back anytime.">Set aside</span>
            ) : null}
          </button>
        ))}
      </div>
      <LowResDialog rejected={rejected} onClose={() => setRejected([])} onPickAnother={() => { setRejected([]); input.current.click(); }} />
    </div>
  );
}
